
import Vue from "vue";
import { required } from "vuelidate/lib/validators";
import ShowSpinners from "@/common/components/ShowSpinners.vue";
import { FormRvsOnlineReportTypeEnum } from "@/features/model/enums/FormMcaOnlineReportTypeEnum";
import { NotificationMemberReport, ReportMember, ReportMemberItem } from "@interface/rvsNotificationReport";

export default Vue.extend({
  name: "CreateRvsOnlineReport",
  components: { ShowSpinners },

  computed: {
    dialog: {
      get() {
        return this.$store.state.openRvsOnlineReportFormModal.open;
      },
      set(val) {
        this.$store.dispatch("openRvsOnlineReportFormModal", {
          ...this.$store.state.openRvsOnlineReportFormModal,
          open: val,
        });
      },
    },

    isSubmitDisabled() {
      if (!this.isAutoSendMonthlyReport) return false;
      return this.rvsScheduleTime === null || this.selectedEmails.length === 0;
    },

    companyId() {
      return this.$store.state.company.id;
    },
  },

  data() {
    const reportTypeList = [{ id: "daily", name: FormRvsOnlineReportTypeEnum.DAILY }];

    const generateScheduleTimeList = () => {
      const list = [];
      for (let h = 0; h < 24; h++) {
        for (let m of [0, 30]) {
          const hh = h.toString().padStart(2, "0");
          const mm = m.toString().padStart(2, "0");
          list.push({ id: `${hh}:${mm}:00`, name: `${hh}:${mm}` });
        }
      }
      return list;
    };

    return {
      reportTypeList,
      currentReportType: { id: "daily", name: FormRvsOnlineReportTypeEnum.DAILY },

      isAutoSendMonthlyReport: false,
      formRvsOnlineReportTypeEnum: FormRvsOnlineReportTypeEnum,

      emailList: [] as ReportMemberItem[],
      selectedEmails: [] as ReportMemberItem[],
      searchText: "",
      isNoSearchData: false,

      page: 1,
      pageSize: 50,
      hasMore: true,
      isFetching: false,
      observer: null,

      isLoading: false,
      loadingMessage: { title: "", detail: "" },

      rvsScheduleTime: null,
      scheduleTimeList: generateScheduleTimeList(),
    };
  },

  mounted() {
    this.currentReportType = { id: "daily", name: FormRvsOnlineReportTypeEnum.DAILY };
  },

  methods: {
    getItemText(item: ReportMemberItem) {
      return item ? `${item.email} (${item.role})` : "";
    },

    resetPaging() {
      this.destroyObserver();
      this.page = 1;
      this.emailList = [];
      this.hasMore = true;
      this.isFetching = false;
    },

    searchEmail() {
      if (this.debounce) clearTimeout(this.debounce);

      this.debounce = setTimeout(async () => {
        this.resetPaging();
        await this.fetchEmailList();
        this.mergeSelectedIntoEmailList();
      }, 300);
    },

    handleOnClearSearchText() {
      this.searchText = "";
      this.resetPaging();

      this.fetchEmailList().then(() => {
        this.mergeSelectedIntoEmailList();
      });
    },

    handleMenuOpen() {
      let attempt = 0;

      const init = () => {
        this.$nextTick(() => {
          const menu = document.querySelector(".lazy-select-menu-rvs .v-list");

          if (!menu && attempt < 4) {
            attempt++;
            return setTimeout(init, 40);
          }

          this.injectFooter();
          this.initObserver();
        });
      };

      init();
    },

    initObserver() {
      const footer = document.querySelector(".lazy-select-footer-rvs");
      if (!footer) return;

      this.destroyObserver();

      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !this.isFetching && this.hasMore) {
          this.fetchEmailList();
        }
      });

      this.observer.observe(footer);
    },

    injectFooter() {
      const menu = document.querySelector(".lazy-select-menu-rvs .v-list");
      if (!menu) return;

      [...menu.querySelectorAll(".lazy-select-footer-rvs")].forEach((e) => e.remove());

      const footer = document.createElement("div");
      footer.className = "lazy-select-footer-rvs grey--text text-center ";
      footer.innerHTML = this.hasMore ? "Loading more…" : "";

      menu.appendChild(footer);
    },

    mergeSelectedIntoEmailList() {
      if (!Array.isArray(this.selectedEmails)) return;

      const set = new Map();

      if (this.searchText) {
        [...this.emailList, ...this.selectedEmails].forEach((item) => {
          if (item && item.id) {
            set.set(item.id, item);
          }
        });
      } else {
        [...this.selectedEmails, ...this.emailList].forEach((item) => {
          if (item && item.id) {
            set.set(item.id, item);
          }
        });
      }

      this.emailList = [...set.values()];
    },

    async fetchEmailList() {
      if (this.isFetching || !this.hasMore) return;

      this.isFetching = true;

      try {
        const response: ReportMember = await this.$dep.modelUseCase.getEmailsByCompanyIdResult(
          this.companyId,
          "RVS",
          this.page,
          this.pageSize,
          this.searchText,
          "daily"
        );

        const newItems = response.items || [];
        this.isNoSearchData = newItems.length === 0;

        this.emailList = this.page === 1 ? [...newItems] : [...this.emailList, ...newItems];

        this.hasMore = newItems.length === this.pageSize;
        if (this.hasMore) this.page++;

        this.mergeSelectedIntoEmailList();

        this.$nextTick(() => {
          this.injectFooter();
          this.initObserver();
        });
      } finally {
        this.isFetching = false;
      }
    },

    async fetchNotificationMembers() {
      const res: NotificationMemberReport = await this.$dep.modelUseCase.getReportNotificationMembers(
        this.companyId,
        "RVS",
        "daily"
      );
      if (res) {
        this.selectedEmails = [...res.data];
      }
    },

    async fetchOnlineReportConfig() {
      const res = await this.$dep.modelUseCase.getOnlineReportConfig(this.companyId, "RVS", "daily");
      if (res) {
        this.isAutoSendMonthlyReport = res.is_auto_send;
        this.rvsScheduleTime = res.interval_time;
      }
    },

    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },

    async handleOnSubmitOnlineReport() {
      this.isLoading = true;

      try {
        const body = {
          memberIds: this.selectedEmails.map((x) => x.id),
          isAutoSend: this.isAutoSendMonthlyReport,
          intervalTime: this.rvsScheduleTime,
        };

        const res = await this.$dep.modelUseCase.updateOnlineReport(this.companyId, body, "RVS", "daily");

        if (!res || res.message !== "success") {
          throw new Error(res?.message || "Unknown error");
        }

        await this.$swal({
          icon: "success",
          title: "Success",
          text: "Report saved successfully.",
          timer: 2000,
          showConfirmButton: false,
        });

        this.dialog = false;
      } catch (err) {
        this.$swal({
          icon: "error",
          title: "Error",
          text: "An error occurred while saving the report.",
          timer: 2000,
          showConfirmButton: false,
        });
      } finally {
        this.isLoading = false;
      }
    },

    resetFormState() {
      this.destroyObserver();
      this.emailList = [];
      // this.selectedEmails = [];
      this.searchText = "";
      this.page = 1;
      this.hasMore = true;
      this.isFetching = false;
    },
  },

  watch: {
    async dialog(open) {
      if (!open) {
        this.resetFormState();
        return;
      }

      this.resetPaging();
      this.isLoading = true;

      try {
        await this.fetchNotificationMembers();
        await this.fetchEmailList();
        this.mergeSelectedIntoEmailList();
        await this.fetchOnlineReportConfig();
      } catch (err) {
        console.error(err);
        this.$swal({
          icon: "error",
          title: "Error",
          text: "Failed to load report settings. Please try again.",
          showConfirmButton: false,
          timer: 1500,
        });
        this.dialog = false;
      } finally {
        this.isLoading = false;
      }
    },
  },

  validations() {
    return {
      currentReportType: { required },
      rvsScheduleTime: { required },
      selectedEmails: { required },
    };
  },
});
